import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import type { IspCustomerRow } from '@/api/isp';

/**
 * Which customer is this connection for?
 *
 * A separate step rather than a dropdown inside the connection form. An operator
 * adding a line from the Connections page is usually looking at a name on a
 * job sheet, and a searchable list of people is a different task from choosing a
 * plan and typing a PPPoE login. Coming the other way — from a customer they
 * just created — this step is skipped entirely, because the answer is known.
 */
interface Props {
   customers: IspCustomerRow[];
   onCancel: () => void;
   onPick: (c: IspCustomerRow) => void;
}

const IspCustomerPicker: React.FC<Props> = ({ customers, onCancel, onPick }) => {
   const [q, setQ] = useState('');
   const shown = useMemo(() => {
      const t = q.trim().toLowerCase();
      if (!t) return customers;
      return customers.filter(
         (c) =>
            c.fullName.toLowerCase().includes(t) ||
            c.customerCode.toLowerCase().includes(t) ||
            c.phone.toLowerCase().includes(t)
      );
   }, [customers, q]);

   return (
      <Wrapper>
         <Search
            autoFocus
            value={q}
            placeholder="Search name, code or number"
            onChange={(e) => setQ(e.target.value)}
         />
         <List>
            {shown.length === 0 && <Empty>Nobody matches that.</Empty>}
            {shown.map((c) => (
               <Row key={c.id} type="button" onClick={() => onPick(c)}>
                  <Name>{c.fullName}</Name>
                  <Meta>
                     {[c.customerCode, c.phone, c.address].filter(Boolean).join(' · ')}
                  </Meta>
                  {/* Somebody who already holds lines is usually the right
                      answer for a SECOND branch, not a mistake. */}
                  {c.accounts > 0 && (
                     <Held>
                        {c.accounts === 1
                           ? 'already has 1 connection'
                           : `already has ${c.accounts} connections`}
                     </Held>
                  )}
               </Row>
            ))}
         </List>
         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
         </Actions>
      </Wrapper>
   );
};

export default IspCustomerPicker;

const Wrapper = styled.div`
   width: 520px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Search = styled.input`
   padding: 9px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
`;

const List = styled.div`
   display: flex;
   flex-direction: column;
   max-height: 340px;
   overflow-y: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const Row = styled.button`
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   gap: 2px;
   padding: 10px 14px;
   background: none;
   border: none;
   border-bottom: 1px solid ${colors.GRAY13};
   cursor: pointer;
   text-align: left;
   &:last-child {
      border-bottom: none;
   }
   &:hover {
      background: ${colors.GRAY14};
   }
`;

const Name = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Meta = styled.span`
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;

const Held = styled.span`
   font-size: 10.5px;
   font-weight: 700;
   color: ${colors.PRIMARY};
`;

const Empty = styled.div`
   padding: 18px;
   text-align: center;
   font-size: 12.5px;
   color: ${colors.GRAY5};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;
